/**
 * Состояния поля — справочник того, чем занято поле в сезоне, когда на нём нет
 * продуктивной культуры: виды пара, АМП, выводное поле, под мелиорацией.
 *
 * До этого экрана новая строка заводилась только миграцией. Тонкий wrapper над
 * `ReferenceFieldCatalog` — как `DiseaseCategories` над `ReferenceMiniCatalog`.
 */
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  ReferenceFieldCatalog,
  type FieldCatalogRow,
} from '@/components/reference/ReferenceFieldCatalog';
import {
  useArchiveFieldCatalogItem,
  useCreateFieldCatalogItem,
  useFieldCatalogAdminList,
  useRestoreFieldCatalogItem,
  useUpdateFieldCatalogItem,
} from './hooks/useCatalogQueries';
import { toast } from '@/lib/toast';

export function sowingsPlural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'посевов';
  if (m10 === 1) return 'посев';
  if (m10 >= 2 && m10 <= 4) return 'посева';
  return 'посевов';
}

export function PlatformAdminFieldStatesPage() {
  const q = useFieldCatalogAdminList('field-states');
  const createMut = useCreateFieldCatalogItem('field-states');
  const updateMut = useUpdateFieldCatalogItem('field-states');
  const archiveMut = useArchiveFieldCatalogItem('field-states');
  const restoreMut = useRestoreFieldCatalogItem('field-states');

  const items: FieldCatalogRow[] = q.data?.items ?? [];
  const active = items.filter((i) => i.is_active).length;

  return (
    <PlatformAdminLayout>
      <ReferenceFieldCatalog
        title="Состояния поля"
        subtitle={`Справочник · ${active} в работе, ${items.length - active} в архиве`}
        createLabel="Добавить состояние"
        usageUnit={sowingsPlural}
        items={items}
        isLoading={q.isLoading}
        onCreate={async (payload) => {
          try {
            await createMut.mutateAsync(payload);
            toast.success('Состояние добавлено');
          } catch (err) {
            toast.error((err as Error).message);
            throw err;
          }
        }}
        onUpdate={async (id, payload) => {
          try {
            await updateMut.mutateAsync({ id, payload });
            toast.success('Состояние сохранено');
          } catch (err) {
            toast.error((err as Error).message);
            throw err;
          }
        }}
        onArchive={async (id) => {
          try {
            await archiveMut.mutateAsync(id);
            toast.success('Состояние убрано в архив');
          } catch (err) {
            toast.error((err as Error).message);
            throw err;
          }
        }}
        onRestore={async (id) => {
          try {
            await restoreMut.mutateAsync(id);
            toast.success('Состояние возвращено из архива');
          } catch (err) {
            toast.error((err as Error).message);
            throw err;
          }
        }}
      />
      <p className="mt-3 text-[11px] text-muted-foreground">
        Записи из архива не предлагаются при заполнении, но остаются подписью у
        прошлых сезонов — поэтому их не удаляют.
      </p>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminFieldStatesPage;
